
import { defineComponent, onMounted, reactive } from 'vue'

// @ui-kit
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'

// @types
import { CityT } from '@/core/types/cities.types'

// @new-services
import locationAPI from '@/new-services/location.service'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { Zone } from '@/new-services/types/location'
import { UserT } from '@/core/types/users.types'
import { users } from '@/core/realisations/users.realisation'
import { DataState } from '@/core/classes/DataState.class'

const DIRECTION_TYPES = [
    { id: 1, name: 'Родительское', value: '1' },
    { id: 2, name: 'Дочернее', value: '2' },
    { id: 3, name: 'Обычное', value: '0' },
]

const STATUS_TYPES = [
    { id: 1, name: 'Активно', value: '1' },
    { id: 2, name: 'Не активно', value: '0' },
]

export default defineComponent({
    components: {
        NewInput,
        NewSelect,
        NewButton,
        NewCheckbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        //

        const state = reactive({
            fromCityId: null as number | null,
            whereCityId: null as number | null,
            zoneId: null as number | null,
            logistId: null as number | null,
            code: null as string | null,
            directionTypeId: null as string | null,
            isActive: null as number | null,
        })

        const data_state = reactive({
            cities: {
                is_loading: false,
                is_failed: false,
                list: [] as CityT[],
            },
            zones: {
                is_loading: false,
                is_failed: false,
                list: [] as Zone[],
            },
            logists: new DataState<UserT>({}),
        })

        //

        const loadCities = async () => {
            try {
                data_state.cities.is_loading = true

                const response = await locationAPI.getCities()
                data_state.cities.list = response.data.data

                data_state.cities.is_failed = false
            } catch (error) {
                data_state.cities.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка городов произошла ошибка',
                })
            } finally {
                data_state.cities.is_loading = false
            }
        }

        const loadZones = async () => {
            try {
                data_state.zones.is_loading = true

                const response = await locationAPI.getZones()
                data_state.zones.list = response.data.data

                data_state.zones.is_failed = false
            } catch (error) {
                data_state.zones.is_failed = true
                console.error(error)
            } finally {
                data_state.zones.is_loading = false
            }
        }

        const loadLogists = async (page: number, name?: string) => {
            data_state.logists.pagination.current_page = page

            try {
                data_state.logists.is_loading = true

                const logists = await users.getLogists(page, name, undefined, 0)

                data_state.logists.list = logists.data
                data_state.logists.pagination.total_pages = logists.meta.last_page
                data_state.logists.is_failed = false
                data_state.logists.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.logists.failure.catchError(error)

                if (!data_state.logists.failure.error_state.is_cancel) {
                    data_state.logists.is_failed = true
                }
            } finally {
                if (!data_state.logists.failure.error_state.is_cancel) {
                    data_state.logists.is_loading = false
                }
            }
        }

        const loadMoreLogists = async (page: number) => {
            if (page > data_state.logists.pagination.total_pages) return
            data_state.logists.pagination.current_page = page

            try {
                data_state.logists.is_loading = true

                const logists = await users.getLogists(page, undefined, 0)

                data_state.logists.list = data_state.logists.list.concat(logists.data)
                data_state.logists.pagination.total_pages = logists.meta.last_page
                data_state.logists.is_failed = false
                data_state.logists.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.logists.failure.catchError(error)

                if (!data_state.logists.failure.error_state.is_cancel) {
                    data_state.logists.is_failed = true
                }
            } finally {
                if (!data_state.logists.failure.error_state.is_cancel) {
                    data_state.logists.is_loading = false
                }
            }
        }

        const searchLogists = async (search_query: string) => {
            loadLogists(1, search_query)
        }

        const unshiftFindedLogist = async (logist: UserT) => {
            await loadLogists(data_state.logists.pagination.current_page)
            data_state.logists.list.unshift(logist)
        }

        const onFiltersChanged = () => {
            if (props.disabled || props.loading) return

            const params = {
                ...state,
                directionTypeId: state.directionTypeId ? Number(state.directionTypeId) : null,
                isActive: state.isActive ? Number(state.isActive) : state.isActive,
            }

            emit('submit', { ...params })
        }

        const onFiltersReset = () => {
            state.fromCityId = null
            state.whereCityId = null
            state.code = null
            state.zoneId = null
            state.logistId = null
            state.directionTypeId = null
            state.isActive = null

            emit('reset')
        }

        //

        onMounted(() => {
            loadZones()
            loadCities()
            loadLogists(1)
        })

        //

        return {
            state,
            data_state,
            //
            onFiltersChanged,
            onFiltersReset,
            loadZones,
            loadLogists,
            loadMoreLogists,
            searchLogists,
            unshiftFindedLogist,
            loadCities,
            //
            DIRECTION_TYPES,
            STATUS_TYPES,
        }
    },
})
